import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, humanize } from '@/lib/utils';
import { DataTable, FilterField, PageHeader, SearchFilterBar, Tabs } from '@/components/ui/layout';
import { Badge, Button, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { useTranslation } from '@/lib/i18n';
import { LANGUAGE_NAMES } from '@/lib/country';
import { normaliseUiLocale } from '@his/shared';
import { TEMPLATES_RETURN_TO } from './TemplatePage';
import { CHANNEL_LABELS, TEMPLATES_QUERY_KEY, type Template } from './types';

interface Delivery {
  id: string;
  channel: string;
  toAddress: string;
  subject: string | null;
  body: string;
  status: string;
  costCredits: number;
  error: string | null;
  sentAt: string | null;
  createdAt: string;
}

export function NotificationsPage() {
  const shell = useTranslation('shell');
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'templates' ? 'templates' : 'outbox';
  const setTab = (id: string) => setParams(id === 'outbox' ? {} : { tab: id }, { replace: true });
  const [status, setStatus] = useState('');
  const openTemplate = (template: Template | null) =>
    navigate(`${template ? `/notifications/templates/${template.id}/edit` : '/notifications/templates/new'}?returnTo=${encodeURIComponent(TEMPLATES_RETURN_TO)}`);

  const deliveries = useQuery({
    queryKey: ['notifications', status],
    queryFn: () => get<{ data: Delivery[] }>(`/notifications${qs({ status: status || undefined, limit: 100 })}`),
    enabled: tab === 'outbox',
  });

  const templates = useQuery({
    queryKey: TEMPLATES_QUERY_KEY,
    queryFn: () => get<Template[]>('/notifications/templates'),
    enabled: tab === 'templates',
  });

  return (
    <>
      <PageHeader
        title={shell.t('nav.item.notifications')}
        description="What was sent to patients and staff, and the wording it was sent with"
        actions={
          <>
            {tab === 'templates' && can('notification.template.manage') && (
              <Button onClick={() => openTemplate(null)}>New template</Button>
            )}
            {tab === 'outbox' && can('notification.send') && (
              <Button onClick={() => navigate('/notifications/send')}>Send a message</Button>
            )}
          </>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'outbox', label: 'Sent' },
          { id: 'templates', label: 'Templates' },
        ]}
      />

      {tab === 'outbox' && (
        <>
          <SearchFilterBar
            filters={
              <FilterField label="Status">
                <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">Every message</option>
                  {['queued', 'sent', 'delivered', 'failed'].map((s) => (
                    <option key={s} value={s}>
                      {humanize(s)}
                    </option>
                  ))}
                </Select>
              </FilterField>
            }
            onReset={status ? () => setStatus('') : undefined}
          />

          <DataTable
            rows={deliveries.data?.data}
            loading={deliveries.isLoading} refreshing={deliveries.isFetching}
            error={deliveries.error}
            rowKey={(row) => row.id}
            empty="Nothing has been sent yet. Appointment reminders and critical results appear here once they go out."
            columns={[
              { header: 'When', sort: (row) => row.sentAt ?? row.createdAt, cell: (row) => dateTime(row.sentAt ?? row.createdAt) },
              {
                header: 'Channel',
                sort: (row) => row.channel,
                cell: (row) => <Badge variant="secondary">{CHANNEL_LABELS[row.channel] ?? row.channel}</Badge>,
              },
              { header: 'To', sort: (row) => row.toAddress, cell: (row) => row.toAddress },
              {
                header: 'Message',
                cell: (row) => (
                  <div className="max-w-md">
                    {row.subject && <p className="font-medium">{row.subject}</p>}
                    <p className="truncate text-xs text-muted-foreground">{row.body}</p>
                    {row.error && <p className="text-xs text-destructive">{row.error}</p>}
                  </div>
                ),
              },
              { header: 'Credits', align: 'right', sort: (row) => row.costCredits, cell: (row) => row.costCredits },
              { header: 'Status', sort: (row) => row.status, cell: (row) => <StatusBadge status={row.status} /> },
              {
                header: '',
                cell: (row) =>
                  row.status === 'failed' && can('notification.send') ? <RetryButton id={row.id} /> : null,
              },
            ]}
          />
        </>
      )}

      {tab === 'templates' && (
        <DataTable
          rows={templates.data}
          loading={templates.isLoading} refreshing={templates.isFetching}
          error={templates.error}
          rowKey={(row) => row.id}
          onRowClick={can('notification.template.manage') ? (row) => openTemplate(row) : undefined}
          empty="No templates yet. A template is the wording a reminder or an alert goes out with."
          columns={[
            { header: 'Code', width: '160px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
            { header: 'Name', sort: (row) => row.name, cell: (row) => row.name },
            {
              header: 'Channel',
              sort: (row) => row.channel,
              cell: (row) => <Badge variant="secondary">{CHANNEL_LABELS[row.channel] ?? row.channel}</Badge>,
            },
            {
              header: 'Language',
              sort: (row) => row.locale,
              cell: (row) => <span className="text-xs">{LANGUAGE_NAMES[normaliseUiLocale(row.locale)]}</span>,
            },
            {
              header: 'Wording',
              cell: (row) => <span dir="auto" className="line-clamp-2 max-w-md text-xs text-muted-foreground">{row.body}</span>,
            },
            {
              header: 'Fills in',
              cell: (row) =>
                row.variables.length ? (
                  <span className="text-xs text-muted-foreground">{row.variables.join(', ')}</span>
                ) : (
                  '—'
                ),
            },
            {
              header: 'Status',
              sort: (row) => row.isActive,
              cell: (row) => (
                <Badge variant={row.isActive ? 'success' : 'muted'}>{row.isActive ? 'in use' : 'off'}</Badge>
              ),
            },
          ]}
        />
      )}
    </>
  );
}

function RetryButton({ id }: { id: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: () => post(`/notifications/${id}/retry`),
    onSuccess: () => {
      toast.success('Queued again');
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (caught) => toast.error('Could not queue it', describeError(caught)),
  });

  return (
    <Button size="sm" variant="outline" loading={retry.isPending} onClick={() => retry.mutate()}>
      Send again
    </Button>
  );
}
